import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import type { TaskListItem } from '@bliks-io/contracts';
import { Button, createColumnHeader, type DataTableColumnDef } from '@bliks-io/ui';
import { formatDate } from '@/lib/formatters';
import { approvalIdOfRef } from '@/components/approvals/ApprovalDialog';

interface UseOpenTasksColumnsParams {
  onComplete: (task: TaskListItem) => void;
  /** Opens the Freigabe a task is about (`source.ref` = `approval:<id>`). */
  onReview: (approvalId: string) => void;
  /** The task whose completion is in flight; its button shows the spinner. */
  pendingTaskId: string | null;
}

/** Columns of an open-Aufgaben table: what, from whom, by when, where to act, done. */
export function useOpenTasksColumns({
  onComplete,
  onReview,
  pendingTaskId
}: UseOpenTasksColumnsParams) {
  const { t, i18n } = useTranslation();

  return useMemo<DataTableColumnDef<TaskListItem>[]>(
    () => [
      {
        accessorKey: 'title',
        meta: { label: t('tasks.columns.title'), filterType: 'text' },
        header: createColumnHeader(t('tasks.columns.title')),
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="font-medium">{row.original.title}</div>
            {row.original.description && (
              <div className="mt-0.5 text-sm text-muted-foreground">{row.original.description}</div>
            )}
          </div>
        ),
        enableColumnFilter: true
      },
      {
        accessorKey: 'createdByLabel',
        meta: { label: t('tasks.columns.createdBy'), filterType: 'text' },
        header: createColumnHeader(t('tasks.columns.createdBy')),
        enableColumnFilter: true
      },
      {
        accessorKey: 'dueAt',
        meta: { label: t('tasks.columns.dueAt') },
        header: createColumnHeader(t('tasks.columns.dueAt')),
        // Locale-dependent, so formatted in the cell rather than the accessor
        // (the accessor value is what the column sorts on).
        cell: ({ row }) => (
          <span className="whitespace-nowrap">{formatDate(row.original.dueAt, i18n.language)}</span>
        )
      },
      {
        id: 'source',
        accessorFn: (row) => (row.source.kind === 'app' ? (row.source.ref ?? '') : ''),
        meta: { label: t('tasks.columns.source') },
        header: createColumnHeader(t('tasks.columns.source')),
        cell: ({ row }) => {
          const source = row.original.source;
          if (source.kind !== 'app') return null;
          return source.url ? (
            <a
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-info-strong hover:underline"
            >
              {source.ref ?? t('tasks.openSource')}
              <ExternalLink className="size-3.5" />
            </a>
          ) : (
            <span className="text-sm text-muted-foreground">{source.ref}</span>
          );
        },
        enableSorting: false
      },
      {
        id: 'actions',
        meta: { sticky: 'right' },
        header: () => <div className="sr-only">{t('common.Actions')}</div>,
        cell: ({ row }) => {
          const approvalId =
            row.original.source.kind === 'app' ? approvalIdOfRef(row.original.source.ref) : null;
          return (
            <div className="flex justify-end gap-2">
              {approvalId && (
                <Button size="small" variant="outline" onClick={() => onReview(approvalId)}>
                  {t('tasks.review')}
                </Button>
              )}
              <Button
                size="small"
                variant="primary"
                loading={pendingTaskId === row.original._id}
                disabled={pendingTaskId !== null}
                onClick={() => onComplete(row.original)}
              >
                {t('tasks.markDone')}
              </Button>
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false
      }
    ],
    [t, i18n.language, onComplete, onReview, pendingTaskId]
  );
}
